import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Audit } from '@vsolv/core/audit/domain';
import { User } from '@vsolv/core/users/domain';
import { Warranty } from '@wsphere/warranties/domain';
import { Observable, catchError, firstValueFrom, lastValueFrom, of, toArray } from 'rxjs';
import { formatDateLocalYYYYMMDD } from '../utils/date.utils';

@Injectable()
export class WarrantyWebService {
  constructor(private http: HttpClient) {}

  async retrieveWarrantyFees(id: string) {
    return await firstValueFrom(this.http.get<Warranty.WarrantyCoverageFee.Model[]>(`/api/warranties/${id}/fees`));
  }

  getOneByCustomerEmail(email: string) {
    return firstValueFrom(
      this.http.get<Warranty.Model>(`/api/warranties/email`, { params: { email } }).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  async getWarrantiesByCustomer(email: string) {
    return lastValueFrom(
      this.http.get<Warranty.Model>(`/api/warranties/email`, { params: { email } }).pipe(
        catchError(err => {
          console.log(err);
          return of(null);
        }),
        toArray()
      )
    );
  }

  getOneById(id: string) {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`/api/warranties/${id}`, {
          params: {
            idField: 'id',
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  getOneByContractNumber(contractNumber: string) {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`/api/warranties/${contractNumber}`, {
          params: {
            idField: 'contractNumber',
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  getOneByAssetId(assetId: string) {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`/api/warranties/${assetId}`, {
          params: {
            idField: 'assetId',
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  getOneByWarrantyId(warrantyId: string) {
    return firstValueFrom(
      this.http
        .get<Warranty.Model>(`/api/warranties/${warrantyId}`, {
          params: {
            idField: 'id',
          },
        })
        .pipe(
          catchError(err => {
            if (err.status === 404) return of(null);
            throw err;
          })
        )
    );
  }

  getOneWithClaims(id: string) {
    return firstValueFrom(
      this.http.get<Warranty.RetrieveWithClaimsResponse>(`/api/warranties/${id}/with-claims`).pipe(
        catchError(err => {
          if (err.status === 404) return of(null);
          throw err;
        })
      )
    );
  }

  list(params: Warranty.ListRequest & { includeAllProperties?: boolean }): Promise<Warranty.ListResponse> {
    const apiParams = {
      ...(params.userId && { userId: params.userId }),
      ...(params.status && { status: params.status }),
      ...(params.assetId && { assetId: params.assetId }),
      ...(params.page ? { page: params.page.toString() } : {}),
      ...(params.policyIds && { policyIds: params.policyIds }),
      ...(params.limit ? { limit: params.limit.toString() } : {}),
      ...(params.customerId && { customerId: params.customerId }),
      ...(params.search && { search: params.search }),
      ...(params.distributorId && { distributorId: params.distributorId }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortOrder && { sortOrder: params.sortOrder }),
      ...(params.startDate &&
        (() => {
          const formatted = formatDateLocalYYYYMMDD(params.startDate);
          return formatted && { startDate: formatted };
        })()),
      ...(params.endDate &&
        (() => {
          const formatted = formatDateLocalYYYYMMDD(params.endDate);
          return formatted && { endDate: formatted };
        })()),
      ...(params.includeAllProperties !== undefined && {
        includeAllProperties: params.includeAllProperties,
      }),
    };

    return firstValueFrom(
      this.http.get<Warranty.ListResponse>(`/api/warranties`, {
        params: apiParams,
      })
    );
  }

  list$(
    params: Warranty.ListRequest & { includeAllProperties?: boolean; cursor?: string }
  ): Observable<Warranty.ListResponse> {
    const apiParams = {
      ...(params.userId && { userId: params.userId }),
      ...(params.status && { status: params.status }),
      ...(params.assetId && { assetId: params.assetId }),
      ...(params.page ? { page: params.page.toString() } : {}),
      ...(params.policyIds && { policyIds: params.policyIds }),
      ...(params.limit ? { limit: params.limit.toString() } : {}),
      ...(params.customerId && { customerId: params.customerId }),
      ...(params.search && { search: params.search }),
      ...(params.distributorId && { distributorId: params.distributorId }),
      ...(params.sortBy && { sortBy: params.sortBy }),
      ...(params.sortOrder && { sortOrder: params.sortOrder }),
      ...(params.startDate &&
        (() => {
          const formatted = formatDateLocalYYYYMMDD(params.startDate);
          return formatted && { startDate: formatted };
        })()),
      ...(params.endDate &&
        (() => {
          const formatted = formatDateLocalYYYYMMDD(params.endDate);
          return formatted && { endDate: formatted };
        })()),
      ...(params.includeAllProperties !== undefined && {
        includeAllProperties: params.includeAllProperties,
      }),
      ...(params.cursor && { cursor: params.cursor }),
    };

    return this.http.get<Warranty.ListResponse>(`/api/warranties`, {
      params: apiParams,
    });
  }

  openClaimWarrantyList(params: Warranty.ListRequest): Promise<Warranty.ListResponse> {
    return firstValueFrom(
      this.http.get<Warranty.ListResponse>(`/api/openClaimWarranties`, {
        params: {
          ...(params.page ? { page: params.page.toString() } : {}),
          ...(params.limit ? { limit: params.limit.toString() } : {}),
          ...(params.customerId && { customerId: params.customerId }),
          ...(params.userId && { userId: params.userId }),
          ...(params.assetId && { assetId: params.assetId }),
          ...(params.status && { status: params.status }),
          ...(params.search && { search: params.search }),
          ...(params.policyIds && { policyIds: params.policyIds }),
        },
      })
    );
  }

  openClaimWarrantyList$(params: Warranty.ListRequest): Observable<Warranty.ListResponse> {
    return this.http.get<Warranty.ListResponse>(`/api/openClaimWarranties`, {
      params: {
        ...(params.page ? { page: params.page.toString() } : {}),
        ...(params.limit ? { limit: params.limit.toString() } : {}),
        ...(params.customerId && { customerId: params.customerId }),
        ...(params.userId && { userId: params.userId }),
        ...(params.assetId && { assetId: params.assetId }),
        ...(params.status && { status: params.status }),
        ...(params.search && { search: params.search }),
        ...(params.policyIds && { policyIds: params.policyIds }),
      },
    });
  }

  customerWarrantyList(params: Warranty.ListRequest): Promise<Warranty.ListResponse> {
    return firstValueFrom(
      this.http.get<Warranty.ListResponse>(`/api/customerWarranties`, {
        params: {
          ...(params.page ? { page: params.page.toString() } : {}),
          ...(params.limit ? { limit: params.limit.toString() } : {}),
          ...(params.customerId && { customerId: params.customerId }),
          ...(params.userId && { userId: params.userId }),
          ...(params.assetId && { assetId: params.assetId }),
          ...(params.status && { status: params.status }),
          ...(params.search && { search: params.search }),
          ...(params.policyIds && { policyIds: params.policyIds }),
        },
      })
    );
  }

  customerWarrantyList$(params: Warranty.ListRequest): Observable<Warranty.ListResponse> {
    return this.http.get<Warranty.ListResponse>(`/api/customerWarranties`, {
      params: {
        ...(params.page ? { page: params.page.toString() } : {}),
        ...(params.limit ? { limit: params.limit.toString() } : {}),
        ...(params.customerId && { customerId: params.customerId }),
        ...(params.userId && { userId: params.userId }),
        ...(params.assetId && { assetId: params.assetId }),
        ...(params.status && { status: params.status }),
        ...(params.search && { search: params.search }),
        ...(params.policyIds && { policyIds: params.policyIds }),
      },
    });
  }

  async update(warrantyId: string, body: Warranty.UpdateWarrantyRequest) {
    return await firstValueFrom(
      this.http.patch<Warranty.UpdateWarrantyResponse>(`/api/warranties/${warrantyId}`, body).pipe(
        catchError(err => {
          console.log(err);
          throw err;
        })
      )
    );
  }

  logTermStartAdjustmentReason(
    warrantyId: string,
    payload: { reason: string; previousTermStart: Date | string | null; updatedTermStart: Date | string }
  ) {
    return firstValueFrom(
      this.http.post<{ success: boolean }>(`/api/warranties/${warrantyId}/term-start-adjustment-reason`, {
        reason: payload.reason,
        previousTermStart: payload.previousTermStart,
        updatedTermStart: payload.updatedTermStart,
      })
    );
  }

  async finalize(warrantyId: string, body?: Warranty.FinalizeWarrantyRequest) {
    return await firstValueFrom(
      this.http.post<Warranty.FinalizeWarrantyResponse>(`/api/warranties/${warrantyId}/finalize`, body).pipe(
        catchError(err => {
          console.log(err);
          throw err;
        })
      )
    );
  }

  async delete(warrantyId: string) {
    return await firstValueFrom(
      this.http.delete<Warranty.DeleteWarrantyResponse>(`/api/warranties/${warrantyId}`).pipe(
        catchError(err => {
          console.log(err);
          throw err;
        })
      )
    );
  }

  async activateWarranty(warrantyId: string, body: Warranty.Activate) {
    return await firstValueFrom(
      this.http.post<Warranty.Activate>(`/api/warranties/${warrantyId}/activate`, body)
    ).catch(() => undefined);
  }

  cancelWarranty(warrantyId: string, effectiveDate: Date, reason = '', refundAmount?: number) {
    return firstValueFrom(
      this.http.post<Warranty.CancelWarrantyResponse>(`/api/warranties/${warrantyId}/cancel`, {
        cancellationDate: effectiveDate,
        cancellationReason: reason,
        refundAmount,
      })
    );
  }

  voidWarranty(warrantyId: string, voidReason: string) {
    return firstValueFrom(
      this.http.post<Warranty.VoidWarrantyResponse>(`/api/warranties/${warrantyId}/void`, {
        voidReason,
      })
    );
  }

  async calculateLiabilityLimit(warrantyId: string, coverageIds: string[]) {
    return await firstValueFrom(
      this.http.post<Warranty.CalculateLiabilityLimitResponse[]>(
        `/api/warranties/${warrantyId}/calculate-liability-limit`,
        {
          warrantyId,
          coverageIds,
        }
      )
    );
  }

  async sendWelcomeEmail(warrantyId: string) {
    return await firstValueFrom(this.http.post(`/api/warranties/${warrantyId}/send-welcome-email`, {}));
  }

  getDurationStats(id: string) {
    return firstValueFrom(
      this.http.get<Warranty.RetrieveWarrantyDurationStatsQueryResponse>(`/api/warranties/${id}/duration/stats`)
    );
  }

  listAudit(warrantyId: string, dto?: { page?: number; limit?: number }): Promise<Audit.Model> {
    return firstValueFrom(
      this.http.get<Audit.Model>(`/api/audit/warranty/${warrantyId}`, {
        params: {
          ...(dto?.page ? { page: dto.page } : {}),
          ...(dto?.limit ? { limit: dto.limit } : {}),
        },
      })
    );
  }

  getAuditUser(userId: string): Promise<User.Model | null> {
    if (!userId) return Promise.resolve(null);

    return firstValueFrom(
      this.http.get<User.Model>(`/api/users/${userId}`).pipe(
        catchError(err => {
          console.warn('getAuditUser failed for', userId, err);
          return of(null);
        })
      )
    );
  }
}
